import { useCallback } from 'react';

import { isNil } from 'lodash-es';

import {
  ICartEntry,
  ICombo,
  IItem,
  IItemOption,
  IItemOptionModifier,
  IMenuHookProps,
  IModifierSelection,
  IQuantityBasedPlu,
  ISanityItemOption
} from '@rbi-ctg/menu';
import { ILogger } from 'utils/logger';
import { isCombo, isItem } from 'utils/menu';
import { ModifierComponentStyle } from 'utils/menu/modifiers';
import {
  priceForItemOptionModifierBooleanOrStepper,
  priceForItemOptionModifierSelection
} from 'utils/menu/price';
import {
  IWithVendorConfig,
  PluTypes,
  bestPlusForQuantity,
  concatenateSizePlu,
  getConstantPlu,
  getVendorConfig
} from 'utils/vendor-config';

interface IPriceItemOptionModifierInput {
  item: ICombo | IItem | ICartEntry | null;
  itemOption: ISanityItemOption | IItemOption | null;
  modifier: (IItemOptionModifier & { quantity?: number }) | null;
}

interface IUsePricingFunctionProps extends IMenuHookProps {
  logger: ILogger;
}

export default function usePricingFunction({ logger, prices, vendor }: IUsePricingFunctionProps) {
  /**
   * hack - this is an attempt to reconcile bad data in
   * the ui layer - there is a chance that injection will
   * fail if the price for a plu is not found, but this
   * is a temporary fix to display a derived price.
   * if minPlu.quantity is not 1 this may be a bad price.
   * it will only be used for display - orders must be validated
   * and priced before injection.
   **/
  const priceForQuantityPlu = useCallback(
    (item: IWithVendorConfig, quantity: number = 1) => {
      const vendorConfig = getVendorConfig(item, vendor) as IQuantityBasedPlu;
      if (!vendorConfig) {
        return 0;
      }
      const plus = bestPlusForQuantity(vendorConfig.quantityBasedPlu, prices, quantity);
      if (!plus.length) {
        return 0;
      }
      return plus.reduce((acc, { plu }) => acc + prices[plu], 0);
    },
    [prices, vendor]
  );

  const priceForItemOptionModifier = useCallback(
    ({ item, itemOption, modifier }: IPriceItemOptionModifierInput): number => {
      if (!item || !itemOption || !modifier || isCombo(item)) {
        return 0;
      }

      if (
        !itemOption.componentStyle ||
        itemOption.componentStyle === ModifierComponentStyle.Selector
      ) {
        return priceForItemOptionModifierSelection({
          item,
          itemOption,
          modifier,
          prices,
          vendor
        });
      }

      return priceForItemOptionModifierBooleanOrStepper({
        item,
        itemOption,
        modifier,
        prices,
        vendor
      });
    },
    [prices, vendor]
  );

  const priceForModifierSelections = useCallback(
    (
      item: ICombo | IItem | null,
      quantity: number = 1,
      modifierSelections: Array<IModifierSelection> = []
    ) => {
      if (isItem(item)) {
        return (
          (modifierSelections as Array<IModifierSelection>).reduce<number>(
            (price, { _key, modifier }) => {
              const itemOption = (item.options || []).find(
                ({ _key: itemOptionKey }) => _key === itemOptionKey
              );

              // illegal case, modifier selection
              // does not correspond to this item's
              // options. discard it when pricing
              if (!itemOption) {
                logger.warn({
                  itemId: item._id,
                  message: 'No ItemOption found for modifier',
                  modifier
                });
                return price;
              }

              const modifierPrice = priceForItemOptionModifier({ item, itemOption, modifier });

              return price + modifierPrice;
            },
            0
          ) * quantity
        );
      }

      // @todo implement modifier upcharge on combo slots
      return 0;
    },
    [logger, priceForItemOptionModifier]
  );

  const getPriceForPlus = useCallback(
    (plu: string, mainItemPlu: string | null = null): number => {
      const combinedPlu = `${plu}-${mainItemPlu}`;

      if (mainItemPlu && !isNil(prices[combinedPlu])) {
        return prices[combinedPlu];
      }

      return prices[plu] || 0;
    },
    [prices]
  );

  /**
   * Returns the price of a menu object
   */
  const pricingFunction = useCallback(
    (
      item: IWithVendorConfig,
      quantity?: number,
      modifierSelections: Array<IModifierSelection> = []
    ) => {
      const vendorConfig = getVendorConfig(item, vendor);
      if (!vendorConfig || vendorConfig.pluType === PluTypes.IGNORE) {
        return 0;
      }

      // for quantity based plus: if price exists for plu, use it -
      // otherwise compute best price for quantity selected from
      // available plus
      // Note: if quantity is undefined we should allow
      // `priceForQuantityPlu` to find the minimum quantity
      // plu to use for pricing, so we don't default to 1
      // until after this check
      if (vendorConfig.pluType === PluTypes.QUANTITY) {
        return priceForQuantityPlu(item, quantity);
      }

      const itemQuantity = quantity || 1;

      /**
       * If item is a combo, and there is a main item with a constant plu,
       * we want to check if there exists a price `<comboPlu>-<mainItemPlu>`,
       * and if it does, use that price instead of `<comboPlu>`
       */
      const mainItemPlu =
        isCombo(item) && item.mainItem
          ? getConstantPlu(getVendorConfig(item.mainItem, vendor))
          : null;

      if (vendorConfig.pluType === PluTypes.MULTI_CONSTANT) {
        return (
          (vendorConfig.multiConstantPlus || []).reduce(
            (acc, { plu }) => acc + getPriceForPlus(plu, mainItemPlu),
            0
          ) * itemQuantity
        );
      }

      let price = 0;
      if (vendorConfig.pluType === PluTypes.CONSTANT) {
        price = getPriceForPlus(vendorConfig.constantPlu, mainItemPlu);
      }

      if (vendorConfig.pluType === PluTypes.SIZE_BASED) {
        const plu = concatenateSizePlu(vendorConfig.sizeBasedPlu);
        price = plu ? getPriceForPlus(plu, mainItemPlu) : 0;
      }

      if (vendorConfig.pluType === PluTypes.PARENT_CHILD) {
        price =
          getPriceForPlus(vendorConfig.parentChildPlu.plu, mainItemPlu) +
          getPriceForPlus(vendorConfig.parentChildPlu.childPlu, mainItemPlu);
      }

      if (isItem(item) || isCombo(item)) {
        const toPrice = isItem(item) ? item : item.mainItem;

        return (
          price * itemQuantity +
          priceForModifierSelections(toPrice, itemQuantity, modifierSelections)
        );
      }

      return price * itemQuantity;
    },
    [getPriceForPlus, priceForModifierSelections, priceForQuantityPlu, vendor]
  );

  return { priceForItemOptionModifier, pricingFunction };
}
